/* eslint-disable react/jsx-no-useless-fragment */
import React, { useRef, useState, useCallback, useMemo, useLayoutEffect, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import { dayjs } from 'utils/dayjs';
import { userFilesApi, sessionAgendaApi, userPanelApi, sessionsApi } from 'api';
import { RouterUrlParams } from 'App';
import { useSessionsData } from 'components/userPanel/sessions/Sessions';
import { Icon, IconStyle } from 'components/_new/Icon';
import { dateFormat } from 'utils/dateFormat';
import { SessionHomeworkPayload, SessionPayload, SessionRatingPayload } from 'types/payloads/SessionPayload';
import { useMutation, useQuery } from '@tanstack/react-query';
import { usePanel } from 'contexts/panelContext';
import { useGlobal } from 'contexts/globalContext';
import { AvatarSet } from 'components/_new/AvatarSet';
import { Box, BoxSection, BoxSectionList } from 'components/_new/Box';
import { NavTabs } from 'components/_new/NavTabs';
import { Page } from 'components/_new/Page/Page';
import { Button } from 'components/_new/Button';
import { OverlayElement } from 'components/_new/Overlay';
import { Menu, MenuItemLink } from 'components/_new/Menu';
import { Tag } from 'components/_new/Tag';
import { Line } from 'components/_new/Line';
import { Link } from 'components/_new/Link/Link';
import { useNavigate } from 'hooks/useNavigate';
import { useAuth } from 'contexts/authContext';
import PageMissing from 'components/PageMissing';
import { usePair } from 'contexts/pairContext';
import { userDisplayName } from 'utils/userDisplayName';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { useScope } from 'contexts/scopeContext';
import { SessionReflections } from 'types/Session';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { classNames } from 'primereact/utils';
import { SessionLinkType } from 'types/SessionMeeting';
import { Rating } from './components/Rating';
import { EditSessionDetailsDialog } from './components/EditSessionDetailsDialog';
import { AgendaSubpage } from './subpages/AgendaSubpage';
import { EvaluationMenteeSubpage } from './subpages/EvaluationMenteeSubpage';
import { EvaluationMentorSubpage } from './subpages/EvaluationMentorSubpage';
import { YourNotesSubpage } from './subpages/YourNotestSubpage';
import { HomeworkSubpage } from './subpages/HomeworkSubpage';
import { ReflectionSubpage } from './subpages/ReflectionSubpage';
import { RemoveSessionDialogConfirm } from './components/RemoveSessionDialogConfirm';
import { SendMeetingCalendarDialog } from './components/SendMeetingCalendarDialog';
import { useMapSessionTasksToItems } from './partials/utils/useMapSessionTasksToItems';
import { RecommendationAISubpage } from './subpages/RecommendationAISubpage';
import { MaterialsSubpage } from './subpages/MaterialsSubpage';

type Subpage = {
	key: string;
	to: string;
	hidden?: boolean;
	label: string;
	path: string;
	element: ReactNode;
	badge?: number;
	iconRight?: string;
	iconRightStyle?: IconStyle;
};

const SessionAgenda = () => {
	const { t } = useTranslation();
	const { organizationName, programName, type, sessionId } = useParams() as RouterUrlParams;
	const { hash, search } = useLocation();
	const { toastRef } = useGlobal();
	const { currentUser } = useAuth();
	const { currentProgramMembership, currentApplicationData } = usePanel();
	const { pair } = usePair();
	const { sessionsData, sessiosRefetch, isLoading: sessionsLoading } = useSessionsData();
	const { currentProgram } = useScope();
	const navigate = useNavigate();

	const currentSession = sessionsData.find((session) => session.id === +sessionId);

	const menuButtonRef = useRef(null);
	const menuRef = useRef<OverlayElement>(null);
	const pageContentRef = useRef(null);
	const avatarSetRef = useRef(null);

	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [meetingCalendarDialogOpen, setMeetingCalendarDialogOpen] = useState(false);
	const [removeSessionDialogConfirmOpen, setRemoveSessionDialogConfirmOpen] = useState(false);
	const [replaceElement, setReplaceElement] = useState(false);

	const {
		data: sessionAgenda,
		isFetched: sessionAgendaFetched,
		isLoading: sessionAgendaLoading,
	} = useQuery({
		queryKey: [
			'session-agenda',
			{
				sessionId: currentSession?.id,
				langauge: currentUser?.language,
			},
		],
		queryFn: () =>
			sessionAgendaApi.getSessionAgendaForSessionId(currentSession!.id, pair.id, currentApplicationData.id),
		enabled: Boolean(currentSession?.id),
	});

	const {
		refetch: getSessionQuestionnaires,
		data: questionnairesData,
		isLoading: questionnairesLoading,
	} = useQuery({
		queryKey: ['questionnaires', currentSession?.sessionType],
		queryFn: () =>
			userPanelApi.getQuestionnaires(pair.id, currentProgramMembership.id, currentSession!.sessionType),
		enabled: Boolean(currentSession?.id),
	});

	const {
		data: userFiles = [],
		isLoading: userFilesLoading,
		refetch: getUserFilesRefetch,
	} = useQuery({
		queryKey: [
			'userfiles',
			{
				programMembershipId: currentProgramMembership.id,
				applicationId: currentApplicationData.id,
				pairId: pair.id,
				sessionId: currentSession?.id,
			},
		],
		queryFn: () =>
			userFilesApi.getUserfiles(
				Number(currentProgramMembership?.id),
				Number(currentApplicationData?.id),
				pair.id,
				{
					sessionId: currentSession?.id || null,
				}
			),
		enabled:
			Boolean(currentProgramMembership?.id) && Boolean(currentApplicationData?.id) && Boolean(currentSession?.id),
	});

	const { data: sessionKnowledgeBaseArticles, refetch: sessionKnowkedgeBaseArticlesRefetch } = useQuery({
		queryKey: [
			'KNOWLEDGE_BASE_ARTICLE_LIST_SESSION',
			{
				applicationId: Number(currentApplicationData.id),
				sessionId: currentSession?.id,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		queryFn: () =>
			knowledgeBaseApi.application.getArticlesForSession(Number(currentApplicationData.id), currentSession!.id),
		enabled: Boolean(currentSession?.id),
	});

	const { mutate: updateSession, mutateAsync: updateSessionAsync } = useMutation({
		mutationFn: (data: SessionPayload) =>
			sessionsApi.updateSession(
				Number(currentSession?.pairId),
				currentProgramMembership.id,
				Number(currentSession?.id),
				data
			),
		onSuccess: () => {
			sessiosRefetch();
		},
	});

	const { mutate: updateSessionFinishedState, isLoading: updateSessionFinishedStateLoading } = useMutation({
		mutationFn: () =>
			sessionsApi.updateSessionFinishedState(
				Number(currentSession?.pairId),
				currentProgramMembership.id,
				Number(currentSession?.id)
			),
		onSuccess: () => {
			sessiosRefetch();
		},
	});

	const { mutate: updateSessionRatingMutate, isLoading: updateSessionRatingLoading } = useMutation({
		mutationFn: (data: SessionRatingPayload) =>
			sessionsApi.updateSessionRating(
				Number(currentSession?.pairId),
				currentProgramMembership.id,
				Number(currentSession?.id),
				data
			),
		onSuccess: () => {
			sessiosRefetch();
		},
	});

	const { mutate: updateSessionReflectionsMutate, isLoading: updateSessionReflectionsLoading } = useMutation({
		mutationFn: ({ data, showToast = true }: { data: Partial<SessionReflections>; showToast?: boolean }) =>
			sessionsApi.updateSessionReflections(
				Number(currentSession?.pairId),
				currentProgramMembership.id,
				Number(currentSession?.id),
				data
			),
		onSuccess: (data, variables) => {
			sessiosRefetch();

			if (variables.showToast) {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('userPanel.sessionsNew.reflection.updatedSuccessfully'),
				});
			}
		},
	});

	const { mutate: updateSessionHomeworkMutate, isLoading: updateSessionHomeworkLoading } = useMutation({
		mutationFn: ({ data, showToast = true }: { data: Partial<SessionHomeworkPayload>; showToast?: boolean }) =>
			sessionsApi.updateSessionHomework(
				Number(currentSession?.pairId),
				currentProgramMembership.id,
				Number(currentSession?.id),
				data
			),
		onSuccess: (data, variables) => {
			sessiosRefetch();

			if (variables.showToast) {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('userPanel.sessionsNew.homework.saveMessage'),
				});
			}
		},
	});

	const { mutate: deleteSession } = useMutation({
		mutationFn: () => sessionsApi.deleteSession(pair.id, currentProgramMembership.id, Number(currentSession?.id)),
		onSuccess: () => {
			navigate(`/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session`);
			setTimeout(() => {
				if (sessiosRefetch) {
					sessiosRefetch();
				}
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('userPanel.sessions.removeSuccess'),
				});
			}, 100);
		},
	});

	const hideSectionTimeRange = useMemo(() => {
		if (!currentSession?.date) {
			return true;
		}
		const sessionTime = dayjs(currentSession.date);
		const now = dayjs();
		const shouldShow = now.isAfter(sessionTime) || now.isAfter(sessionTime.subtract(1, 'hour'));
		return !shouldShow;
	}, [currentSession]);

	const reflectionKeys = useMemo(() => {
		switch (type) {
			case 'mentee':
				return [
					'general',
					'thingsEasy',
					'happyFor',
					'thingsHard',
					'feedback',
					'result',
					'nextSteps',
					'other',
					'techniques',
				] as const;
			case 'mentor':
			default:
				return [
					'general',
					'result',
					'techniques',
					'barriers',
					'thingsHard',
					'selfReflection',
					'happyFor',
					'sadFor',
					'other',
				] as const;
		}
	}, [type]);

	const reflections = useMemo(() => {
		return reflectionKeys.map((key) => ({
			key,
			title: t(`userPanel.sessionsNew.reflection.${currentApplicationData.applicationRole}.${key}`),
			value: (currentSession?.reflection as any)?.[key] || null,
		}));
	}, [reflectionKeys, t, currentApplicationData.applicationRole, currentSession?.reflection]);

	const handleRatingChange = useCallback(
		(rating: number) => {
			updateSessionRatingMutate({ rating });
		},
		[updateSessionRatingMutate]
	);

	const handleRatingCommentChange = useCallback(
		(ratingComment: string) => {
			if (currentSession) {
				updateSessionRatingMutate({ rating: currentSession.rating, ratingComment });
			}
		},
		[currentSession, updateSessionRatingMutate]
	);

	const handleReflectionsSave = useCallback(
		(values: Partial<SessionReflections>, showToast = true) => {
			updateSessionReflectionsMutate({ data: values, showToast });
		},
		[updateSessionReflectionsMutate]
	);

	const homeworkSaveHandler = (data: Partial<SessionHomeworkPayload>, showToast = true) =>
		updateSessionHomeworkMutate({ data, showToast });

	useLayoutEffect(() => {
		if (hash && hash.length > 0) {
			const elementId = hash.replace('#', '').trim();
			const element = document.getElementById(elementId);
			if (element) {
				(element as any)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
				navigate({ hash: undefined });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hash]);

	useEffect(() => {
		const searchParams = new URLSearchParams(search);
		if (searchParams.get('dialog') === 'editSession') {
			setTimeout(() => {
				setIsEditModalOpen(true);
				setTimeout(() => {
					searchParams.delete('dialog');
					const newSearch = searchParams.toString();
					const newUrl = `${window.location.pathname}${newSearch.length ? `?${newSearch}` : ''}`;
					window.history.replaceState({}, '', newUrl);
				}, 100);
			}, 1000);
		}
	}, [search]);

	if (!currentSession) return <PageMissing />;
	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setReplaceElement(entry.isIntersecting);
			},
			{ root: pageContentRef.current, threshold: 0.1 }
		);
		const target = avatarSetRef.current;
		if (target) observer.observe(target);
		return () => {
			if (target) observer.unobserve(target);
		};
	}, []);
	const items = useMapSessionTasksToItems(currentSession?.sessionTasks || []) as unknown as MenuItemLink[];

	// Od tego momentu TypeScript wie że currentSession istnieje
	const prepareSessionTypeName = (sessionType: string) => {
		switch (String(sessionType)) {
			case 'first':
				return t('userPanel.sessions.first.fullTitle');
			case 'second':
				return t('userPanel.sessions.second.fullTitle');
			case 'middle':
				return t('userPanel.sessions.middle.fullTitle');
			case 'penultimate':
				return t('userPanel.sessions.penultimate.fullTitle');
			case 'last':
				return t('userPanel.sessions.last.fullTitle');
			case 'standard':
				return t('userPanel.sessions.standard.fullTitle');
			default:
				return '';
		}
	};

	const canNotDelete = sessionsData?.length <= 2;

	const currentSessionIndex = sessionsData.findIndex((session) => session.id === currentSession.id);
	const prevSession = sessionsData[currentSessionIndex - 1];
	const hasPrevSession = Boolean(prevSession?.id);
	const nextSession = sessionsData[currentSessionIndex + 1];
	const hasNextSession = Boolean(nextSession?.id);
	const isLast = currentSession.sessionType === 'last';

	const menteeQuestionnairesData =
		questionnairesData?.filter((questionnaire) => questionnaire.forMentorOnly === false) || undefined;
	const menteeQuestionnaireAnswers = menteeQuestionnairesData?.find(
		(questionnaire) => questionnaire.sessionType === currentSession.sessionType
	)?.questionnaireAnswers;
	const hasMenteeQuestionnaireAnswers =
		menteeQuestionnaireAnswers &&
		Object.entries(menteeQuestionnairesData).every(([k, v]) =>
			// eslint-disable-next-line no-nested-ternary
			typeof v !== 'undefined' && typeof v === 'number'
				? v > 0
				: typeof v === 'string'
				? String(v).length > 0
				: true
		);

	const mentorQuestionnairesData =
		questionnairesData?.filter((questionnaire) => questionnaire.forMentorOnly === true) || undefined;
	const mentorQuestionnaireAnswers = mentorQuestionnairesData?.find(
		(questionnaire) => questionnaire.sessionType === currentSession.sessionType
	)?.questionnaireAnswers;
	const hasMentorQuestionnaireAnswers =
		mentorQuestionnaireAnswers &&
		Object.entries(mentorQuestionnairesData).every(([k, v]) =>
			// eslint-disable-next-line no-nested-ternary
			typeof v !== 'undefined' && typeof v === 'number'
				? v > 0
				: typeof v === 'string'
				? String(v).length > 0
				: true
		);

	let visibleSessionArticles;
	if (sessionKnowledgeBaseArticles) {
		if (currentUser?.isProfessionalViewEnabled) {
			visibleSessionArticles = sessionKnowledgeBaseArticles;
		} else {
			visibleSessionArticles = sessionKnowledgeBaseArticles.filter((item) => !item.isProfessional);
		}
	}

	const subpages: Subpage[] = [
		pair &&
			sessionAgenda && {
				key: 'agenda',
				to: `/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session/${currentSession.id}/agenda`,
				label: t('userPanel.sessionsNew.agenda'),
				path: 'agenda',
				element: (
					<AgendaSubpage
						session={currentSession}
						pair={pair}
						sessionAgenda={sessionAgenda}
						prevSession={prevSession}
						nextSession={nextSession}
					/>
				),
			},
		visibleSessionArticles &&
			visibleSessionArticles.length > 0 && {
				key: 'materials',
				to: `/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session/${currentSession.id}/materials`,
				label: t('userPanel.sessionsNew.materials.title'),
				badge: visibleSessionArticles.length,
				path: 'materials',
				element: (
					<MaterialsSubpage
						articles={sessionKnowledgeBaseArticles}
						refetch={() => {
							sessionKnowkedgeBaseArticlesRefetch();
						}}
					/>
				),
			},
		['middle', 'last'].includes(currentSession.sessionType) && {
			key: 'evaluation-mentee',
			to: `/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session/${currentSession.id}/evaluation-mentee`,
			label: t('userPanel.sessionsNew.evaluation.forMentee'),
			iconRight: !hasMenteeQuestionnaireAnswers ? 'circle-exclamation' : undefined,
			iconRightStyle: 'solid',
			path: 'evaluation-mentee',
			element: (
				<EvaluationMenteeSubpage
					session={currentSession}
					questionnairesData={menteeQuestionnairesData}
					refetchQuestionnaires={() => getSessionQuestionnaires()}
				/>
			),
		},
		mentorQuestionnairesData &&
			type === 'mentor' &&
			['second', 'middle', 'last'].includes(currentSession.sessionType) && {
				key: 'evaluation-mentor',
				hidden: !currentUser?.isProfessionalViewEnabled,
				to: `/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session/${currentSession.id}/evaluation-mentor`,
				label: t('userPanel.sessionsNew.evaluation.forMentor'),
				iconRight: !hasMentorQuestionnaireAnswers ? 'circle-exclamation' : undefined,
				iconRightStyle: 'solid',
				path: 'evaluation-mentor',
				element: (
					<EvaluationMentorSubpage
						session={currentSession}
						questionnairesData={mentorQuestionnairesData}
						refetchQuestionnaires={() => getSessionQuestionnaires()}
					/>
				),
			},
		{
			key: 'notes',
			to: `/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session/${currentSession.id}/notes`,
			label: t('userPanel.sessionsNew.yourNotes.title'),
			badge: userFiles.length,
			path: 'notes',
			element: (
				<YourNotesSubpage
					session={currentSession}
					files={userFiles}
					loading={userFilesLoading}
					getUserFilesRefetch={getUserFilesRefetch}
				/>
			),
		},
		!isLast && {
			key: 'homework',
			hidden: !currentUser?.isProfessionalViewEnabled,
			to: `/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session/${currentSession.id}/homework`,
			label: t('userPanel.sessionsNew.homework.title'),
			path: 'homework',
			element: (
				<HomeworkSubpage
					session={currentSession}
					saveHandler={homeworkSaveHandler}
					loading={updateSessionHomeworkLoading}
				/>
			),
		},
		{
			key: 'reflection',
			hidden: !currentUser?.isProfessionalViewEnabled,
			to: `/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session/${currentSession.id}/reflection`,
			label: t('userPanel.sessionsNew.reflection.tabTitle'),
			path: 'reflection',
			element: (
				<ReflectionSubpage
					session={currentSession}
					reflections={reflections}
					onSave={handleReflectionsSave}
					loading={updateSessionReflectionsLoading}
				/>
			),
		},
		currentProgram?.mentibotEnabled &&
			currentApplicationData?.applicationRole === 'mentor' &&
			currentSession.sessionType !== 'first' && {
				key: 'ai',
				hidden: !currentUser?.isProfessionalViewEnabled,
				to: `/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session/${currentSession.id}/recommendation`,
				label: t('userPanel.sessionsNew.aiRecommendations.title'),
				path: 'recommendation',
				element: <RecommendationAISubpage />,
				iconRight: 'wand-magic-sparkles',
				iconRightStyle: 'solid',
			},
	].filter(Boolean) as Subpage[];

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setReplaceElement(entry.isIntersecting);
			},
			{ root: pageContentRef.current, threshold: 0.1 }
		);
		const target = avatarSetRef.current;
		if (target) observer.observe(target);
		return () => {
			if (target) observer.unobserve(target);
		};
	}, []);

	const isLoadingContent =
		sessionAgendaLoading ||
		questionnairesLoading ||
		!sessionAgendaFetched ||
		(Boolean(currentSession?.id) && !sessionKnowledgeBaseArticles);

	return (
		<>
			<Menu
				ref={menuRef}
				placement="bottom-end"
				items={[
					{
						key: 'edit',
						type: 'button',
						label: t('actions.edit'),
						onClick: () => setIsEditModalOpen(true),
					},
					{
						key: 'remove',
						type: 'button',
						label: t('actions.remove'),
						className: 'text-danger',
						disabled: canNotDelete,
						tooltip: canNotDelete ? t('userPanel.sessions.canNotDeleteSessionTooltip') : undefined,
						onClick: () => setRemoveSessionDialogConfirmOpen(true),
					},
				]}
			/>
			<SendMeetingCalendarDialog
				session={currentSession}
				visible={meetingCalendarDialogOpen}
				onHide={() => setMeetingCalendarDialogOpen(false)}
			/>
			<EditSessionDetailsDialog
				previousSession={prevSession}
				nextSession={nextSession}
				session={currentSession}
				visible={isEditModalOpen}
				onHide={() => setIsEditModalOpen(false)}
				onFinish={() => {
					sessiosRefetch();
				}}
			/>
			<RemoveSessionDialogConfirm
				visible={removeSessionDialogConfirmOpen}
				onHide={() => setRemoveSessionDialogConfirmOpen(false)}
				confirmAction={{
					onClick: () => {
						deleteSession();
					},
				}}
			/>
			<Page
				beforeLeft={
					<div className="flex flex-row align-items-center" style={{ minHeight: 42 }}>
						<Link
							to={`/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session`}
							variant="standard"
							iconOnly={!replaceElement}
							icon="arrow-left"
							iconSet="fa"
							iconStyle="solid"
							noUnderline
							className={classNames('font-bold', { 'ml-1': !replaceElement })}
						>
							{t('userPanel.sessionsNew.backToSessionList')}
						</Link>
						{!replaceElement ? (
							<div className="flex flex-row gap-3 align-items-center">
								<AvatarSet
									ref={avatarSetRef}
									className="hidden sm:inline-flex"
									avatars={[
										{
											name: userDisplayName(pair?.application.programMembership.user, null),
											src: pair?.application.programMembership.user.avatarActive
												? pair?.application.programMembership.user.avatar
												: undefined,
										},
										{
											name: userDisplayName(currentUser!, null),
											src: currentUser?.avatar ? currentUser?.avatar : undefined,
										},
									]}
									interactive
								/>
								<h2 className="inline-block m-0 text-nowrap">{`${t('userPanel.sessionsNew.session')} ${
									currentSession.sessionNumber
								}`}</h2>
								<Tag variant="grey" style={{ marginTop: '3px' }}>
									{prepareSessionTypeName(currentSession.sessionType)}
								</Tag>
							</div>
						) : null}
					</div>
				}
				beforeRight={
					<div className="flex flex-row gap-3 align-items-center py-3">
						<Link
							to={
								hasPrevSession
									? `/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session/${
											sessionsData[currentSessionIndex - 1]?.id
									  }`
									: '#'
							}
							scrollToTop
							className="font-bold"
							icon="angle-left"
							iconSet="fa"
							iconStyle="solid"
							noUnderline
							disabled={!hasPrevSession}
							responsive="sm"
						>
							{t('userPanel.sessionsNew.prevSession')}
						</Link>
						<Line vertical />
						<Link
							to={
								hasNextSession
									? `/panel/${organizationName}/${programName}/${type}/pair/${pair.id}/session/${
											sessionsData[currentSessionIndex + 1]?.id
									  }`
									: '#'
							}
							scrollToTop
							className="font-bold"
							icon="angle-right"
							iconPosition="right"
							iconSet="fa"
							iconStyle="solid"
							noUnderline
							disabled={!hasNextSession}
							responsive="sm"
						>
							{t('userPanel.sessionsNew.nextSession')}
						</Link>
					</div>
				}
				header={
					<div className="flex flex-row justify-content-between align-items-center gap-2">
						<div className="flex flex-row gap-2 md:gap-3 align-items-center">
							<AvatarSet
								ref={avatarSetRef}
								className="hidden sm:inline-flex"
								avatars={[
									{
										name: userDisplayName(pair?.application.programMembership.user, null),
										src: pair?.application.programMembership.user.avatarActive
											? pair?.application.programMembership.user.avatar
											: undefined,
									},
									{
										name: userDisplayName(currentUser!, null),
										src: currentUser?.avatar ? currentUser?.avatar : undefined,
									},
								]}
								interactive
							/>
							<h2 className="inline-block m-0">{`${t('userPanel.sessionsNew.session')} ${
								currentSession.sessionNumber
							}`}</h2>
							<Tag variant="grey" style={{ marginTop: '3px' }}>
								{prepareSessionTypeName(currentSession.sessionType)}
							</Tag>
						</div>
						<Button
							ref={menuButtonRef}
							onClick={() => {
								if (menuRef?.current && menuButtonRef?.current) {
									menuRef.current.toggle(menuButtonRef?.current);
								}
							}}
							label="Menu"
							icon="fa fa-solid fa-ellipsis"
							iconOnly
							rounded
							variant="primary-text"
						/>
					</div>
				}
				scrollable
			>
				{sessionsLoading ? (
					<>
						<div className="flex flex-row flex-wrap gap-4 mb-2">
							<div className="w-full flex-3">
								<div className="flex flex-column gap-4">
									<div className="flex flex-column md:flex-row justify-content-start flex-1 gap-2">
										<div className="flex flex-row flex-1 bold md:py-4 px-2">
											<Skeleton width="80px" height="1.5rem" />
										</div>
										<div className="flex flex-column md:flex-row flex-3 align-items-center gap-2">
											<Skeleton width="100%" height="120px" borderRadius="8px" />
										</div>
									</div>
									<div className="flex flex-column md:flex-row justify-content-start flex-1 gap-2">
										<div className="flex flex-row flex-1 bold md:py-4 px-2">
											<Skeleton width="150px" height="1.5rem" />
										</div>
										<div className="flex flex-column md:flex-row flex-3 align-items-center gap-2">
											<Skeleton width="100%" height="60px" borderRadius="8px" />
										</div>
									</div>
								</div>
							</div>
							<div className="flex-1 flex flex-column gap-4">
								<Skeleton width="100%" height="150px" borderRadius="12px" />
								<Skeleton width="100%" height="200px" borderRadius="12px" />
							</div>
						</div>

						<div className="flex flex-row gap-3 mb-4" style={{ borderBottom: '2px solid #e9ecef' }}>
							{[1, 2, 3, 4].map((item) => (
								<Skeleton key={item} width="120px" height="40px" />
							))}
						</div>

						<div className="flex flex-column gap-3" style={{ minHeight: '500px', paddingBottom: '1rem' }}>
							<Skeleton width="100%" height="60px" />
							<Skeleton width="100%" height="200px" />
							<Skeleton width="100%" height="150px" />
							<Skeleton width="80%" height="40px" />
						</div>
					</>
				) : (
					<>
						<div className="flex flex-row flex-wrap gap-4 mb-2 md:mb-4">
							<div className="w-full flex-3">
								<div className="flex flex-column gap-4">
									<div className="flex flex-column md:flex-row justify-content-start flex-1 gap-2">
										<div className="flex flex-row flex-1 bold md:py-4 px-2">
											{t('userPanel.sessionsNew.deadline')}
										</div>
										<div className="flex flex-column md:flex-row flex-3 align-items-center gap-2">
											<div className="relative flex-1">
												{!currentSession.date && (
													<div className="box-warning-icon">
														<Icon
															name="exclamation-circle"
															className="warning-icon-color"
														/>
													</div>
												)}
												<Box
													variant={!currentSession.date ? 'blue-bordered' : 'blue'}
													vertical
													full
													slim
													className={!currentSession.date ? 'warning-border' : ''}
												>
													<BoxSection
														className="min-w-0 text-nowrap sm:min-w-1"
														contentClassName="flex flex-row align-items-center gap-2"
													>
														<Button
															icon="calendar-day"
															iconOnly
															label={t('actions.edit')}
															variant="primary-text"
															variantSize="sm"
															onClick={() => setIsEditModalOpen(true)}
														/>
														<span>
															{currentSession.date
																? dateFormat(currentSession.date, 'dayAndMonthPlusYear')
																: '-'}
														</span>
													</BoxSection>
													<BoxSection
														className="text-nowrap ml-4 sm:ml-0"
														contentClassName="flex flex-row align-items-center gap-2"
													>
														<Button
															icon="clock"
															iconOnly
															label={t('actions.edit')}
															variant="primary-text"
															variantSize="sm"
															onClick={() => setIsEditModalOpen(true)}
														/>
														<span>
															{currentSession.date
																? dateFormat(currentSession.date, 'hours')
																: '-'}
														</span>
													</BoxSection>
													<Button
														className="mr-3"
														icon="pen"
														iconOnly
														label={t('actions.edit')}
														variant="primary-text"
														variantSize="sm"
														onClick={() => setIsEditModalOpen(true)}
													/>
												</Box>
											</div>
											<div className="flex flex-column">
												{currentSession.date && dayjs().isAfter(currentSession.date) ? (
													// eslint-disable-next-line react/jsx-no-useless-fragment
													<>
														{currentSession.finished ? (
															<Button
																disabled
																label={t('userPanel.sessionsNew.sessionIsFinished')}
																icon="check"
																variant="success-text"
																variantSize="sm"
															/>
														) : (
															<Button
																label={t('userPanel.sessionsNew.markAsFinished')}
																icon="info-circle"
																variant="primary-text"
																variantSize="sm"
																disabled={!currentSession.date}
																loading={updateSessionFinishedStateLoading}
																onClick={() => {
																	updateSessionFinishedState();
																}}
															/>
														)}
													</>
												) : (
													<>
														{currentSession.date &&
															currentSession.sessionMeeting?.linkType !==
																SessionLinkType.CALENDAR && (
																<Button
																	label={t(
																		currentSession.invitationSentAt
																			? 'userPanel.sessionsNew.sendReminderAfter'
																			: 'userPanel.sessionsNew.sendReminder'
																	)}
																	icon={
																		currentSession.invitationSentAt
																			? 'check'
																			: 'plus'
																	}
																	variant={
																		currentSession.invitationSentAt
																			? 'success-text'
																			: 'primary-text'
																	}
																	variantSize="sm"
																	disabled={!currentSession.date}
																	onClick={() => setMeetingCalendarDialogOpen(true)}
																	tooltip={
																		currentSession.invitationSentAt
																			? t(
																					'userPanel.sessionsNew.sendReminderTooltip'
																			  )
																			: undefined
																	}
																/>
															)}
													</>
												)}
											</div>
										</div>
									</div>
									{currentSession.sessionMeeting &&
										currentSession.sessionMeeting.link !== null &&
										dayjs().add(-2, 'hours').isBefore(currentSession.date) && (
											<div className="flex flex-column md:flex-row justify-content-start flex-1 gap-2">
												<div className="flex flex-row flex-1 bold md:py-4 px-2">
													{t('userPanel.sessions.meetingLinkLabelAgenda')}
												</div>
												<div className="flex flex-column md:flex-row flex-3 align-items-center gap-2">
													<Box variant="blue" full wrap slim>
														<BoxSection
															contentClassName="flex flex-row align-items-center gap-2 overflow-hidden"
															onClick={() =>
																window.open(
																	String(currentSession.sessionMeeting?.link),
																	'_blank'
																)
															}
															style={{ cursor: 'pointer' }}
														>
															<Button
																variant="primary-text"
																variantSize="sm"
																label={t(
																	currentSession.sessionMeeting?.linkType ===
																		SessionLinkType.INTERNAL
																		? 'userPanel.sessions.meetingLinkLabelInternal'
																		: 'userPanel.sessions.meetingLinkLabel'
																)}
																icon="external-link"
																iconOnly
															/>
															<div className="flex-1 text-elipsis">
																{t(
																	currentSession.sessionMeeting?.linkType ===
																		SessionLinkType.INTERNAL
																		? 'userPanel.sessions.meetingLinkLabelInternal'
																		: 'userPanel.sessions.meetingLinkLabel'
																)}
															</div>
														</BoxSection>
													</Box>
												</div>
											</div>
										)}
									<div className="flex flex-column md:flex-row justify-content-start flex-1 gap-2">
										<div className="flex flex-row flex-1 bold md:py-4 px-2">
											{t('userPanel.sessionsNew.purposeOfTheMeeting')}
										</div>
										<div className="flex flex-column md:flex-row flex-3 align-items-center gap-2">
											<Box variant="blue" full wrap slim>
												<BoxSection
													// className="text-nowrap"
													contentClassName="flex flex-row align-items-center gap-2 overflow-hidden"
												>
													<Button
														icon="pencil"
														iconOnly
														label={t('actions.edit')}
														variant="primary-text"
														variantSize="sm"
														onClick={() => setIsEditModalOpen(true)}
														className="flex-0"
													/>
													<div className="flex-1 text-elipsis">
														{currentSession.goal || '-'}
													</div>
												</BoxSection>
											</Box>
										</div>
									</div>

									{!hideSectionTimeRange && (
										<div className="flex flex-column md:flex-row justify-content-start flex-1 gap-2">
											<div className="flex flex-row flex-1 bold md:py-4 px-2">
												{t('userPanel.sessionsNew.rateSession')}
											</div>
											<div className="flex flex-column md:flex-row flex-3 align-items-center gap-2">
												<Tooltip
													target="#rate-session"
													content={t('userPanel.sessionsNew.rateSessionHint')}
													position="bottom"
													style={{ maxWidth: 220 }}
												/>
												<div id="rate-session" className="w-full">
													<Box variant="blue" full wrap slim>
														<BoxSection contentClassName="flex flex-row align-items-center gap-2 py-1 pl-3 pr-2">
															<Rating
																value={currentSession.rating}
																onChange={handleRatingChange}
																onCommentChange={handleRatingCommentChange}
																comment={currentSession.ratingComment}
																loading={updateSessionRatingLoading}
															/>
														</BoxSection>
													</Box>
												</div>
											</div>
										</div>
									)}
								</div>
							</div>
							<div className="flex-1 flex flex-column gap-4">
								{currentSession.sessionTasks?.length !== 0 && (
									<Box variant="white-bordered">
										<BoxSectionList header={t(`userPanel.sessions.doBeforeSession`)}>
											{items?.map((item) => (
												<div key={item.key}>
													<Link
														key={item.key}
														noUnderline
														icon={item?.icon}
														iconClassName="text-purple"
														variant="grey"
														to={item?.to}
													>
														<span>{item?.label}</span>
													</Link>
												</div>
											))}
										</BoxSectionList>
									</Box>
								)}
							</div>
						</div>
						{isLoadingContent ? (
							<>
								<div
									className="flex flex-row gap-3 mb-4 pb-2"
									style={{ borderBottom: '2px solid #e9ecef' }}
								>
									{[1, 2, 3, 4].map((item) => (
										<Skeleton key={item} width="120px" height="40px" />
									))}
								</div>
								<div
									className="flex flex-column gap-3"
									style={{ minHeight: '500px', paddingBottom: '1rem' }}
								>
									<Skeleton width="100%" height="60px" />
									<Skeleton width="100%" height="200px" />
									<Skeleton width="100%" height="150px" />
									<Skeleton width="80%" height="40px" />
								</div>
							</>
						) : (
							<>
								<NavTabs
									id="tabs"
									tabs={subpages.map(
										({ key, hidden, to, label, badge, iconRight, iconRightStyle }) => ({
											key,
											to,
											hidden,
											label,
											badge,
											iconRight,
											iconRightStyle,
										})
									)}
									minusMargin
								/>

								<div id="content" style={{ minHeight: '500px', paddingBottom: '1rem' }}>
									<Routes>
										<Route index element={<Navigate to={subpages[0].path} replace />} />
										{subpages.map(({ path, element }) => (
											<Route key={path} path={path} element={element} />
										))}
									</Routes>
								</div>
							</>
						)}
					</>
				)}
			</Page>
		</>
	);
};

export default SessionAgenda;
